import { useState, useEffect } from 'react';
import { useAudioSettings } from '../context/AudioSettingsContext';
import { useTheme } from '../context/ThemeContext';

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const VolumeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
  </svg>
);

const MusicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const Toggle = ({ value, onChange }) => (
  <button
    style={{
      ...styles.toggle,
      backgroundColor: value ? 'var(--accent)' : 'var(--bg-hover)',
    }}
    onClick={() => onChange(!value)}
  >
    <div style={{
      ...styles.toggleKnob,
      transform: value ? 'translateX(20px)' : 'translateX(0)',
    }} />
  </button>
);

const AudioVideoSettings = ({ onClose }) => {
  const { settings, updateSetting, resetToDefaults } = useAudioSettings();
  const { theme } = useTheme();

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>Audio Settings</h3>
          <button style={styles.closeButton} onClick={onClose}>
            <XIcon />
          </button>
        </div>

        <div style={styles.body}>
          {/* Music Toggle */}
          <div style={styles.settingRow}>
            <div style={styles.settingLeft}>
              <div style={{...styles.iconWrap, backgroundColor: 'rgba(14, 165, 233, 0.15)', color: '#0ea5e9'}}>
                <MusicIcon />
              </div>
              <div style={styles.settingInfo}>
                <span style={styles.settingLabel}>Music</span>
                <span style={styles.settingDesc}>
                  {settings.musicEnabled ? 'Background music on' : 'Background music off'}
                </span>
              </div>
            </div>
            <Toggle
              value={settings.musicEnabled}
              onChange={(val) => updateSetting('musicEnabled', val)}
            />
          </div>

          {/* Main Volume Slider */}
          {settings.musicEnabled && (
            <div style={styles.sliderSection}>
              <div style={styles.sliderHeader}>
                <VolumeIcon />
                <span style={styles.sliderLabel}>Volume</span>
                <span style={styles.sliderValue}>{Math.round(settings.mainVolume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(settings.mainVolume * 100)}
                onChange={e => updateSetting('mainVolume', parseInt(e.target.value) / 100)}
                style={styles.slider}
              />
            </div>
          )}

          {/* Sound Effects Toggle */}
          <div style={styles.settingRow}>
            <div style={styles.settingLeft}>
              <div style={{...styles.iconWrap, backgroundColor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6'}}>
                <VolumeIcon />
              </div>
              <div style={styles.settingInfo}>
                <span style={styles.settingLabel}>Sound Effects</span>
                <span style={styles.settingDesc}>
                  {settings.soundEnabled ? 'Button sounds on' : 'Button sounds off'}
                </span>
              </div>
            </div>
            <Toggle
              value={settings.soundEnabled}
              onChange={(val) => updateSetting('soundEnabled', val)}
            />
          </div>

          {/* Reset to Defaults */}
          <button style={styles.resetButton} onClick={resetToDefaults}>
            <RefreshIcon />
            <span>Reset to Defaults</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  modal: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    width: '100%',
    maxWidth: '400px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
  },
  title: {
    color: 'var(--text)',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  body: {
    padding: '16px',
    overflowY: 'auto',
    flex: 1,
  },
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid var(--border)',
  },
  settingLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  iconWrap: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  settingInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  settingLabel: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  settingDesc: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  toggle: {
    width: '48px',
    height: '28px',
    borderRadius: '14px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.2s ease',
    flexShrink: 0,
  },
  toggleKnob: {
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    position: 'absolute',
    top: '2px',
    left: '2px',
    transition: 'transform 0.2s ease',
  },
  sliderSection: {
    padding: '8px 0 12px 48px',
    borderBottom: '1px solid var(--border)',
  },
  sliderHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    color: 'var(--text-secondary)',
  },
  sliderLabel: {
    color: 'var(--text-secondary)',
    fontSize: '13px',
    flex: 1,
  },
  sliderValue: {
    color: 'var(--accent)',
    fontSize: '13px',
    fontWeight: '600',
    minWidth: '36px',
    textAlign: 'right',
  },
  slider: {
    width: '100%',
    height: '4px',
    WebkitAppearance: 'none',
    appearance: 'none',
    background: 'var(--border)',
    borderRadius: '2px',
    outline: 'none',
    cursor: 'pointer',
    accentColor: 'var(--accent)',
  },
  resetButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    marginTop: '16px',
    backgroundColor: 'transparent',
    border: '1px solid var(--warning)',
    borderRadius: '8px',
    color: 'var(--warning)',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

export default AudioVideoSettings;
